import React, { useState, useEffect, useCallback } from 'react'
import { X, UserCheck, AlertCircle, Loader2 } from 'lucide-react'
import { apiService } from '../../services/api'
import StyledSelect from '../shared/StyledSelect'
import toast from 'react-hot-toast'

interface ResponsibleUser {
  id: string
  name: string
  email: string
  type: string
  store: string
  active: boolean
}

interface TransferResponsibilityModalProps {
  isOpen: boolean
  onClose: () => void
  onRequestTransfer: (newResponsibleId: string, comments?: string) => Promise<void>
  requestId: string
  currentResponsibleName?: string
  equipmentDescription?: string
  storeId?: string
  departmentId?: string
}

export default function TransferResponsibilityModal({
  isOpen,
  onClose,
  onRequestTransfer,
  requestId,
  currentResponsibleName,
  equipmentDescription,
  storeId,
  departmentId
}: TransferResponsibilityModalProps) {
  const [responsibleUsers, setResponsibleUsers] = useState<ResponsibleUser[]>([])
  const [selectedUserId, setSelectedUserId] = useState('')
  const [comments, setComments] = useState('')
  const [loading, setLoading] = useState(false)
  const [fetchingUsers, setFetchingUsers] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Buscar usuários elegíveis quando o modal abrir.
  // Usa o requestId (fonte de verdade = ativo) para o backend resolver
  // loja + departamento corretos; aceita fallback explícito storeId/departmentId.
  const fetchResponsibleUsers = useCallback(async () => {
    try {
      setFetchingUsers(true)
      setError(null)

      const response = await apiService.getResponsibleUsers({
        requestId,
        storeId,
        departmentId
      })
      const users = response || []

      setResponsibleUsers(users)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar usuários')
    } finally {
      setFetchingUsers(false)
    }
  }, [requestId, storeId, departmentId])

  useEffect(() => {
    if (isOpen && requestId) {
      fetchResponsibleUsers()
    }
  }, [isOpen, requestId, fetchResponsibleUsers])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!selectedUserId) {
      setError('Selecione um novo responsável')
      return
    }

    try {
      setLoading(true)
      setError(null)
      
      await onRequestTransfer(selectedUserId, comments)
      
      toast.success('Responsabilidade transferida com sucesso!', {
        duration: 3000,
        position: 'top-right',
      })
      
      // Resetar formulário
      setSelectedUserId('')
      setComments('')
      
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao transferir responsabilidade')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading && !fetchingUsers) {
      setSelectedUserId('')
      setComments('')
      setError(null)
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Overlay */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />
        
        {/* Modal */}
        <div className="relative w-full max-w-md transform rounded-lg bg-white p-6 shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-brand-primary-500" />
              <h2 className="text-lg font-semibold text-brand-text-primary">
                Transferir Responsabilidade
              </h2>
            </div>
            <button
              onClick={handleClose}
              disabled={loading || fetchingUsers}
              className="rounded-full p-1 text-gray-400 hover:bg-brand-surface-100 hover:text-brand-text-secondary disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="space-y-4">
            {/* Informações da solicitação */}
            <div className="bg-brand-surface-50 p-3 rounded-lg">
              <p className="text-sm font-medium text-brand-text-primary mb-1">
                {equipmentDescription || `Solicitação #${requestId}`}
              </p>
              {currentResponsibleName && (
                <p className="text-sm text-brand-text-secondary">
                  Responsável atual: <span className="font-medium">{currentResponsibleName}</span>
                </p>
              )}
            </div>

            {/* Formulário */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Seleção de novo responsável */}
              <div>
                <label className="block text-sm font-medium text-brand-text-primary mb-1">
                  Novo Responsável *
                </label>
                {fetchingUsers ? (
                  <div className="flex items-center gap-2 text-sm text-brand-text-muted">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Carregando usuários...
                  </div>
                ) : (
                  <StyledSelect
                    options={responsibleUsers.map((user) => ({
                      value: user.id,
                      label: user.name,
                      subtitle: user.type
                    }))}
                    value={selectedUserId}
                    onChange={setSelectedUserId}
                    placeholder="Selecione um usuário..."
                    required
                    name="newResponsibleId"
                    disabled={loading}
                  />
                )}
              </div>

              {/* Comentários (opcional) */}
              <div>
                <label className="block text-sm font-medium text-brand-text-primary mb-1">
                  Comentários (opcional)
                </label>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  rows={3}
                  placeholder="Adicione um comentário sobre esta transferência..."
                  className="w-full rounded-md border-brand-neutral-300 shadow-sm focus:border-brand-primary-500 focus:ring-brand-primary-500"
                  disabled={loading}
                />
              </div>

              {/* Erro */}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                  <AlertCircle className="h-4 w-4 text-red-400" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Botões */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading || fetchingUsers}
                  className="flex-1 px-4 py-2 text-sm font-medium text-brand-text-primary bg-white border border-brand-neutral-300 rounded-md hover:bg-brand-surface-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary-500 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading || fetchingUsers || !selectedUserId}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-brand-primary-500 border border-transparent rounded-md hover:bg-brand-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Transferindo...
                    </div>
                  ) : (
                    'Confirmar Transferência'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}